import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import {
  CheckCircleIcon,
  CreditCardIcon,
  QrCodeIcon,
  ShieldCheckIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';

export default function MemberCheckoutPage() {
  const { gymId } = useParams();
  const [gymInfo, setGymInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [memberId, setMemberId] = useState('');
  const [amount, setAmount] = useState(1500);
  const [paymentMethod, setPaymentMethod] = useState('GPay UPI');
  const [statusMsg, setStatusMsg] = useState('');

  useEffect(() => {
    fetchGymInfo();
  }, [gymId]);

  const fetchGymInfo = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/payments/checkout-info/${gymId || 1}`);
      setGymInfo(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = async (e) => {
    e.preventDefault();
    if (!memberId || !amount) {
      alert('Please enter your Member ID and Amount');
      return;
    }
    try {
      const res = await api.post('/api/payments/member-pay', {
        gym_id: gymId || 1,
        member_id: memberId,
        amount: amount,
        payment_method: paymentMethod
      });
      setStatusMsg(res.data.message);
    } catch (err) {
      alert('Payment submission failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="text-gray-500 font-semibold">Loading Gym Payment Checkout...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-4">
      <div className="bg-white text-gray-900 rounded-3xl max-w-md w-full p-8 shadow-2xl space-y-6">
        {/* Header */}
        <div className="text-center border-b border-gray-100 pb-4">
          <div className="w-12 h-12 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-2">
            <BuildingStorefrontIcon className="w-6 h-6 text-orange-600" />
          </div>
          <h1 className="text-xl font-black text-gray-900">{gymInfo?.gym_name || 'Gym Membership Payment'}</h1>
          <p className="text-xs text-gray-500 mt-1">Direct Secure Payment Gateway</p>
        </div>

        {statusMsg ? (
          <div className="bg-green-100 border border-green-300 text-green-800 p-4 rounded-2xl text-center space-y-2">
            <CheckCircleIcon className="w-10 h-10 text-green-600 mx-auto" />
            <h2 className="font-bold text-md">Payment Successful!</h2>
            <p className="text-xs">{statusMsg}</p>
          </div>
        ) : (
          <form onSubmit={handlePayNow} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Your Member ID</label>
              <input
                type="number"
                required
                placeholder="Enter Member ID (e.g. 1)"
                value={memberId}
                onChange={(e) => setMemberId(e.target.value)}
                className="w-full text-sm border p-3 rounded-xl focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Membership Renewal Amount (₹)</label>
              <input
                type="number"
                required
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="w-full text-sm border p-3 rounded-xl focus:ring-2 focus:ring-orange-500 font-bold text-green-700"
              />
            </div>

            {/* Merchant Gateway Info */}
            <div className="bg-gray-50 p-4 rounded-2xl border border-gray-200 text-center space-y-2">
              <span className="text-[10px] font-extrabold uppercase text-gray-500 tracking-wider">Merchant Payment Destination</span>
              <p className="font-extrabold text-sm text-gray-900">{gymInfo?.payment_settings?.merchant_name || gymInfo?.gym_name}</p>
              {gymInfo?.payment_settings?.upi_id && (
                <p className="text-xs font-mono text-orange-600 bg-white p-2 rounded-xl border border-orange-200">
                  GPay / UPI ID: {gymInfo.payment_settings.upi_id}
                </p>
              )}
            </div>

            <button
              type="submit"
              className="w-full py-3.5 bg-orange-600 hover:bg-orange-700 text-white font-extrabold rounded-2xl shadow-lg transition-all text-sm flex items-center justify-center gap-2"
            >
              <ShieldCheckIcon className="w-5 h-5 text-orange-200" /> Confirm & Pay ₹{amount}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
